import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Gift, Star, Sparkles, Award, Clock, TrendingUp, Copy, Check, Users, Share2,
  PartyPopper, Lock, Ticket, CreditCard, Zap, RefreshCw, Trash2,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import useMessageStream from '../hooks/useMessageStream';
import { GiftCardsList } from './MyGiftCards';
import { AddonRedeemPanel } from '../components/AddonRedeemPanel';
import { TabCard } from '../components/TabCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const addonImg = (url) => (!url ? null : url.startsWith('http') ? url : `${API_URL}${url}`);
const fmtDay = (d) => {
  if (!d) return '';
  try { return new Date(`${d}T00:00:00`).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }); }
  catch { return d; }
};

// Collapsible accordion card lives in components/TabCard.jsx (shared).

export const MyLoyalty = () => {
  const [data, setData] = useState(null);
  const [addonCtx, setAddonCtx] = useState(null);
  const [giftCards, setGiftCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeemingId, setRedeemingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [copiedCode, setCopiedCode] = useState('');
  const [redeemDialog, setRedeemDialog] = useState({ open: false, bookingId: null });
  const [historyPage, setHistoryPage] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const openTab = searchParams.get('tab') || 'rewards';
  const setTab = (k) => {
    const p = new URLSearchParams(searchParams);
    if (openTab === k) p.delete('tab'); else p.set('tab', k);
    setSearchParams(p, { replace: true });
  };

  const fetchAll = async () => {
    try {
      const [loy, add, gc] = await Promise.all([
        axios.get(`${API_URL}/api/customer/loyalty`, { withCredentials: true }),
        axios.get(`${API_URL}/api/customer/loyalty/redeemable-addons`, { withCredentials: true }),
        axios.get(`${API_URL}/api/customer/gift-cards`, { withCredentials: true }),
      ]);
      setData(loy.data);
      setAddonCtx(add.data);
      setGiftCards(gc.data || []);
    } catch (e) {
      if (e.response?.status === 401) window.location.href = '/login';
      else toast.error('Failed to load loyalty data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Live sync: admin grant/redeem on the CRM customer profile reflects here.
  useMessageStream((evt) => {
    if (!evt) return;
    if (evt.type === 'customer_updated') fetchAll();
  });

  const balance = data?.balance || 0;
  const myRedemptions = addonCtx?.my_redemptions || [];
  const vouchers = data?.active_vouchers || [];
  const addons = addonCtx?.addons || [];
  const rewards = data?.rewards || [];
  const history = data?.history || [];
  const activeRewardsCount = myRedemptions.length + vouchers.length;
  const redeemCount = addons.length + rewards.length;
  const promoActive = data?.earning_rate_source === 'promo' && data?.active_promo;

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(''), 1500);
  };

  const redeemReward = async (reward) => {
    if (balance < reward.points_cost) {
      toast.error(`You need ${reward.points_cost - balance} more points`);
      return;
    }
    if (!window.confirm(`Redeem "${reward.name}" for ${reward.points_cost} points?`)) return;
    setRedeemingId(reward.reward_id);
    try {
      const res = await axios.post(`${API_URL}/api/customer/loyalty/redeem`,
        { reward_id: reward.reward_id }, { withCredentials: true });
      const v = res.data.voucher;
      if (v?.discount_code) toast.success(`Code ${v.discount_code} is ready — apply it at checkout.`);
      else toast.success('Freebie added to your account — auto-applies on your next booking.');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Redemption failed');
    } finally { setRedeemingId(null); }
  };

  const removeRedemption = async (r) => {
    if (!window.confirm(`Remove "${r.addon_name}" and refund ${r.points_cost} points?`)) return;
    setRemovingId(r.redemption_id);
    try {
      await axios.delete(`${API_URL}/api/customer/loyalty/redeem-addon/${r.redemption_id}`, { withCredentials: true });
      toast.success(`Removed — ${r.points_cost} points refunded`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove reward');
    } finally { setRemovingId(null); }
  };

  const openRedeem = (bookingId = null) => setRedeemDialog({ open: true, bookingId });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF8]">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" />
        </div>
      </div>
    );
  }

  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(history.length / pageSize));
  const safePage = Math.min(historyPage, totalPages - 1);
  const pageRows = history.slice(safePage * pageSize, safePage * pageSize + pageSize);

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-4xl">

        {/* Hero card — pinned */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl p-6 sm:p-8 text-white"
          style={{ background: 'linear-gradient(135deg, #FF3B6B 0%, #FB923C 100%)' }}
          data-testid="loyalty-hero"
        >
          <div className="absolute -right-10 -top-10 opacity-20"><Sparkles className="w-40 h-40" /></div>
          <div className="flex items-center gap-2 text-white/90 text-sm"><Award className="w-4 h-4" /> Your Loyalty Points</div>
          <p className="text-5xl sm:text-6xl font-bold mt-2" style={{ fontFamily: 'Fredoka' }} data-testid="loyalty-balance">
            {balance.toLocaleString()} <span className="text-2xl opacity-80">pts</span>
          </p>
          <p className="text-white/90 text-sm mt-1" data-testid="loyalty-earning-rate">
            Earn {data?.earning_rate ?? 2} point{(data?.earning_rate === 1) ? '' : 's'} for every $1 spent.
          </p>
          {promoActive && (
            <p className="mt-2 inline-block bg-white/20 rounded-full px-3 py-1 text-xs font-semibold" data-testid="loyalty-promo-note">
              🎉 {data.active_promo.name} — {data.earning_rate} pts/$1{data.active_promo.end_date ? ` until ${data.active_promo.end_date}` : ''}!
            </p>
          )}
        </motion.div>

        {/* Invite Friends — pinned */}
        {data?.referral_code && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="mt-5 rounded-3xl p-5 sm:p-6 bg-white border border-[#E8E5DF] relative overflow-hidden"
            data-testid="referral-card"
          >
            <div className="absolute -right-8 -bottom-8 opacity-10"><Users className="w-36 h-36 text-[#6366F1]" /></div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-9 h-9 rounded-xl bg-[#6366F1]/10 text-[#6366F1] flex items-center justify-center"><Share2 className="w-4 h-4" /></div>
              <div>
                <h2 className="text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Invite friends, earn {data.referral_bonus_points || 100} pts</h2>
                <p className="text-xs text-[#6B6B7A]">We credit you {data.referral_bonus_points || 100} pts the moment a friend books with your code.</p>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <div className="flex-1 w-full flex items-center gap-2">
                <div className="flex-1 font-mono text-base sm:text-lg font-semibold text-[#1E1E24] bg-[#FFFDF8] border border-dashed border-[#E8E5DF] rounded-xl px-3 py-2.5 truncate" data-testid="referral-code">
                  {data.referral_code}
                </div>
                <button
                  onClick={() => { copyCode(data.referral_code); toast.success('Code copied'); }}
                  className="min-h-[44px] px-3 rounded-xl border border-[#E8E5DF] hover:bg-[#FFFDF8] text-sm font-medium text-[#1E1E24] flex items-center gap-1"
                  data-testid="copy-referral-code"
                >
                  {copiedCode === data.referral_code ? <Check className="w-4 h-4 text-[#06D6A0]" /> : <Copy className="w-4 h-4" />} Copy
                </button>
              </div>
              <Button
                onClick={async () => {
                  const url = `${window.location.origin}/book?ref=${data.referral_code}`;
                  try {
                    if (navigator.share) await navigator.share({ title: 'Book a party with my code', text: `Use my Creative Birthdays referral code ${data.referral_code}`, url });
                    else { await navigator.clipboard.writeText(url); toast.success('Share link copied!'); }
                  } catch { /* cancelled */ }
                }}
                className="rounded-full bg-[#6366F1] hover:bg-[#4F46E5] text-white px-5 min-h-[44px]"
                data-testid="share-referral-btn"
              >
                <Share2 className="w-4 h-4 mr-1.5" /> Share link
              </Button>
            </div>
            <div className="mt-4 pt-3 border-t border-[#E8E5DF] flex items-center justify-between text-sm">
              <span className="text-[#6B6B7A]">Friends referred</span>
              <span className="font-semibold text-[#1E1E24]" data-testid="referrals-count">
                {data.referrals_count || 0}
                {data.referrals_earned > 0 && <span className="text-[#06D6A0] font-normal ml-2">(+{data.referrals_earned.toLocaleString()} pts earned)</span>}
              </span>
            </div>
          </motion.div>
        )}

        {/* Accordion tab cards */}
        <div className="mt-6 space-y-3">

          {/* 4.1 Active Rewards */}
          <TabCard
            id="rewards" open={openTab === 'rewards'} onToggle={() => setTab('rewards')}
            icon={<Ticket className="w-5 h-5 text-[#06D6A0]" />} accent="bg-[#06D6A0]/10"
            title="Your Active Rewards" subtitle="Rewards applied to your upcoming parties"
            badge={activeRewardsCount || null} badgeTone="green"
          >
            {activeRewardsCount === 0 ? (
              <div className="text-center py-6" data-testid="active-rewards-empty">
                <p className="text-sm text-[#6B6B7A] mb-3">You have no active rewards. Redeem your points below!</p>
                <Button onClick={() => setTab('redeem')} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="go-to-redeem-btn">
                  <Gift className="w-4 h-4 mr-1.5" /> Redeem your points
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {myRedemptions.map((r) => (
                  <div key={r.redemption_id} className="flex items-center justify-between gap-3 rounded-xl border border-[#06D6A0]/30 bg-[#06D6A0]/5 p-3" data-testid={`active-reward-${r.redemption_id}`}>
                    <div className="min-w-0 flex items-center gap-3">
                      <div className="w-11 h-11 rounded-lg bg-white border border-[#E8E5DF] overflow-hidden flex items-center justify-center flex-none">
                        {addonImg(r.image_url) ? <img src={addonImg(r.image_url)} alt={r.addon_name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <Gift className="w-5 h-5 text-[#FB923C]" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#1E1E24] truncate">{r.addon_name} <span className="text-[#058B69] font-normal">— free</span></p>
                        <p className="text-[11px] text-[#6B6B7A] truncate">{(r.child_name || 'party')}'s party · {fmtDay(r.booking_date)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-none">
                      <Button size="sm" variant="outline" onClick={() => openRedeem(r.booking_id)} className="rounded-full min-h-[40px] border-[#E8E5DF]" data-testid={`change-reward-${r.redemption_id}`}>
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> Change
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => removeRedemption(r)} disabled={removingId === r.redemption_id} className="rounded-full min-h-[40px] text-[#FF3B6B] hover:bg-[#FFE9EE]" data-testid={`remove-reward-${r.redemption_id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> {removingId === r.redemption_id ? '…' : 'Remove'}
                      </Button>
                    </div>
                  </div>
                ))}
                {vouchers.map((v) => (
                  <div key={v.voucher_id} className="rounded-xl border border-[#FF3B6B]/25 bg-[#FF3B6B]/5 p-3 flex items-center justify-between gap-3" data-testid={`active-voucher-${v.voucher_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#1E1E24] truncate">{v.reward_name}</p>
                      {v.discount_code ? (
                        <div className="mt-1 flex items-center gap-2">
                          <span className="font-mono text-sm bg-white rounded px-2 py-0.5 border border-[#E8E5DF]" data-testid={`voucher-code-${v.voucher_id}`}>{v.discount_code}</span>
                          <button onClick={() => copyCode(v.discount_code)} className="text-xs text-[#FF3B6B] hover:underline flex items-center gap-1" data-testid={`copy-code-${v.voucher_id}`}>
                            {copiedCode === v.discount_code ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                          </button>
                        </div>
                      ) : (
                        <p className="text-xs text-[#06D6A0] mt-1">Auto-applies at checkout</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabCard>

          {/* 4.2 Redeem Your Points */}
          <TabCard
            id="redeem" open={openTab === 'redeem'} onToggle={() => setTab('redeem')}
            icon={<Gift className="w-5 h-5 text-[#FF3B6B]" />} accent="bg-[#FF3B6B]/10"
            title="Redeem Your Points" subtitle="Turn your points into party perks and savings"
            badge={redeemCount || null}
          >
            {/* Party add-ons */}
            <div className="flex items-center gap-2 mb-3">
              <PartyPopper className="w-4 h-4 text-[#FB923C]" />
              <h3 className="text-sm font-semibold text-[#1E1E24]">Party add-ons <span className="font-normal text-[#6B6B7A]">— free on an upcoming party</span></h3>
            </div>
            {addons.length === 0 ? (
              <p className="text-sm text-[#6B6B7A] italic mb-5">No add-ons available to redeem right now.</p>
            ) : (
              <div className="grid gap-3 mb-6 [grid-template-columns:repeat(auto-fill,minmax(140px,1fr))]" data-testid="redeem-addons-grid">
                {addons.map((a) => {
                  const afford = a.affordable;
                  const src = addonImg(a.image_url);
                  return (
                    <div
                      key={a.reward_id}
                      onClick={() => { if (afford) openRedeem(null); }}
                      className={`rounded-2xl border bg-white p-3 text-center transition-all ${afford ? 'border-[#E8E5DF] hover:border-[#FF3B6B] hover:-translate-y-0.5 cursor-pointer' : 'border-[#E8E5DF] opacity-70'}`}
                      data-testid={`redeem-addon-card-${a.reward_id}`}
                    >
                      <div className="w-20 h-20 mx-auto rounded-xl bg-[#FFF4E6] overflow-hidden flex items-center justify-center">
                        {src ? <img src={src} alt={a.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <Gift className="w-8 h-8 text-[#FB923C]" />}
                      </div>
                      <p className="mt-2 text-xs font-semibold text-[#1E1E24] truncate" title={a.name}>{a.name}</p>
                      <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-[#B8860B]"><Star className="w-3 h-3 text-[#E8A80E]" /> {a.points_cost.toLocaleString()} pts</span>
                      <div className={`mt-2 w-full rounded-full py-1.5 text-xs font-semibold flex items-center justify-center gap-1 ${afford ? 'bg-[#FF3B6B] text-white' : 'bg-gray-100 text-[#9a9aa6]'}`}>
                        {afford ? 'Redeem' : <><Lock className="w-3 h-3" /> {a.deficit.toLocaleString()} more</>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Discounts & freebies */}
            {rewards.length > 0 && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-[#FF3B6B]" />
                  <h3 className="text-sm font-semibold text-[#1E1E24]">Discounts &amp; freebies</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {rewards.map((r) => {
                    const afford = balance >= r.points_cost;
                    return (
                      <div key={r.reward_id} className={`bg-white rounded-2xl border p-4 ${afford ? 'border-[#E8E5DF]' : 'border-[#E8E5DF] opacity-70'}`} data-testid={`reward-${r.reward_id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${r.type === 'discount' ? 'bg-[#FF3B6B]/10 text-[#FF3B6B]' : 'bg-[#06D6A0]/10 text-[#06D6A0]'}`}>
                            {r.type === 'discount' ? <TrendingUp className="w-5 h-5" /> : <Gift className="w-5 h-5" />}
                          </div>
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#FFFDF8] border border-[#E8E5DF] text-[#1E1E24] flex items-center gap-1">
                            <Star className="w-3 h-3 text-[#E8A80E]" /> {r.points_cost} pts
                          </span>
                        </div>
                        <p className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>{r.name}</p>
                        {r.description && <p className="text-xs text-[#6B6B7A] mt-1 line-clamp-2">{r.description}</p>}
                        <Button onClick={() => redeemReward(r)} disabled={!afford || redeemingId === r.reward_id}
                          className={`mt-3 w-full rounded-full min-h-[40px] ${afford ? 'bg-[#FF3B6B] hover:bg-[#E62E58] text-white' : 'bg-gray-100 text-[#9a9aa6]'}`}
                          data-testid={`redeem-${r.reward_id}`}>
                          {redeemingId === r.reward_id ? 'Redeeming…' : afford ? 'Redeem' : `Need ${(r.points_cost - balance).toLocaleString()} more`}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {redeemCount === 0 && <p className="text-sm text-[#6B6B7A] italic">No rewards available yet. Check back soon!</p>}
          </TabCard>

          {/* 4.3 Points History */}
          <TabCard
            id="history" open={openTab === 'history'} onToggle={() => setTab('history')}
            icon={<Clock className="w-5 h-5 text-[#6366F1]" />} accent="bg-[#6366F1]/10"
            title="Points History" subtitle="Every point earned and redeemed"
            badge={history.length || null} badgeTone="pink"
          >
            {history.length === 0 ? (
              <p className="text-sm text-[#6B6B7A] italic py-4 text-center" data-testid="history-empty">No points history yet.</p>
            ) : (
              <>
                <div className="bg-white rounded-xl border border-[#E8E5DF] overflow-hidden" data-testid="history-list">
                  {pageRows.map((h) => (
                    <div key={h.ledger_id} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E8E5DF] last:border-b-0" data-testid={`history-row-${h.ledger_id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[#1E1E24]">{h.description}</p>
                        <p className="text-[11px] text-[#9a9aa6] flex flex-wrap items-center gap-x-2">
                          <span>{(h.created_at || '').slice(0, 10)}</span>
                          {h.booking_reference && (
                            <Link to="/my-bookings" className="font-mono text-[#FF3B6B] hover:underline" data-testid={`history-ref-${h.ledger_id}`}>{h.booking_reference}</Link>
                          )}
                        </p>
                      </div>
                      <span className={`font-semibold flex-shrink-0 ${h.points >= 0 ? 'text-[#06D6A0]' : 'text-[#FF3B6B]'}`} data-testid={`history-amount-${h.ledger_id}`}>
                        {h.points >= 0 ? '+' : ''}{h.points.toLocaleString()} pts
                      </span>
                    </div>
                  ))}
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-3" data-testid="history-pagination">
                    <Button size="sm" variant="outline" disabled={safePage === 0} onClick={() => setHistoryPage(safePage - 1)} className="rounded-full min-h-[40px]" data-testid="history-prev">
                      <ChevronLeft className="w-4 h-4" /> Prev
                    </Button>
                    <span className="text-xs text-[#6B6B7A]">Page {safePage + 1} of {totalPages}</span>
                    <Button size="sm" variant="outline" disabled={safePage >= totalPages - 1} onClick={() => setHistoryPage(safePage + 1)} className="rounded-full min-h-[40px]" data-testid="history-next">
                      Next <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabCard>

          {/* 4.4 Gift Cards */}
          <TabCard
            id="giftcards" open={openTab === 'giftcards'} onToggle={() => setTab('giftcards')}
            icon={<CreditCard className="w-5 h-5 text-[#FB923C]" />} accent="bg-[#FB923C]/10"
            title="Gift Cards" subtitle="Cards you've purchased or received"
            badge={giftCards.length || null} badgeTone="amber"
          >
            <div className="flex justify-end mb-3">
              <Link to="/gift-cards" className="text-sm text-[#FF3B6B] font-semibold hover:underline" data-testid="loyalty-buy-gift-card">+ Buy a gift card</Link>
            </div>
            <GiftCardsList cards={giftCards} onChanged={fetchAll} />
          </TabCard>

          {/* 4.5 Earning Rate & Promotions */}
          <TabCard
            id="promos" open={openTab === 'promos'} onToggle={() => setTab('promos')}
            icon={<Zap className="w-5 h-5 text-[#E8A80E]" />} accent="bg-[#FFF4E6]"
            title="Earning Rate & Promotions" subtitle="How fast you earn points"
            badge={promoActive ? 'Promo' : null} badgeTone="green"
          >
            <div className="flex items-center justify-between rounded-2xl bg-[#FFFDF8] border border-[#E8E5DF] p-4" data-testid="earning-rate-card">
              <div>
                <p className="text-xs text-[#6B6B7A] uppercase tracking-wide">Current earning rate</p>
                <p className="text-[11px] text-[#9a9aa6] mt-0.5">{promoActive ? 'Boosted by an active promotion' : 'Standard rate'}</p>
              </div>
              <p className="text-3xl font-bold text-[#FF3B6B]" style={{ fontFamily: 'Fredoka' }} data-testid="earning-rate-value">
                {data?.earning_rate ?? 2} <span className="text-base font-medium text-[#6B6B7A]">pts / $1</span>
              </p>
            </div>
            {promoActive && (
              <div className="mt-3 rounded-2xl p-4 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #06D6A0 0%, #059669 100%)' }} data-testid="promo-banner">
                <Sparkles className="absolute -right-4 -top-4 w-24 h-24 opacity-20" />
                <p className="text-sm font-bold" style={{ fontFamily: 'Fredoka' }}>🎉 {data.active_promo.name}</p>
                <p className="text-xs text-white/90 mt-1">
                  Earning {data.earning_rate} pts for every $1{data.active_promo.end_date ? ` — ends ${data.active_promo.end_date}` : ''}. Book now to make the most of it!
                </p>
              </div>
            )}
            {!promoActive && (
              <p className="text-xs text-[#6B6B7A] mt-3">No promotions running right now — we'll highlight the next bonus-points event here.</p>
            )}
          </TabCard>
        </div>

        <div className="mt-8 text-center">
          <Link to="/book" className="text-[#FF3B6B] font-semibold hover:underline">Book your next party →</Link>
        </div>
      </div>

      {/* Add-on redemption modal (shared) */}
      <Dialog open={redeemDialog.open} onOpenChange={(o) => setRedeemDialog((s) => ({ ...s, open: o }))}>
        <DialogContent className="max-w-xl bg-white max-h-[88vh] overflow-y-auto" data-testid="loyalty-redeem-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>Redeem a free add-on</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B6B7A] -mt-2 mb-2">
            Use your points to add an extra to one of your upcoming parties at no charge. Remove it anytime to get your points back.
          </p>
          {redeemDialog.open && (
            <AddonRedeemPanel variant="dialog" lockedBookingId={redeemDialog.bookingId} onChanged={fetchAll} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyLoyalty;
